import React from 'react';
import { RGCaptcha } from 'react-geetest-captcha';
import { useTranslation } from 'react-i18next';
import { LOCAL_STORAGE } from '@constants';
import { CAPTCHA_NAME } from './Captcha.constants';
import { useCaptcha } from './withCaptcha';
import { GeetestSolution } from './Captcha.types';
import useNetwork from './useNetwork';

type GeetestUnprocessedSolution = {
  geetest_challenge: string;
  geetest_validate: string;
  geetest_seccode: string;
};

type CaptchaProps = {
  onSuccess?: (solution: GeetestSolution) => void;
};

const langMapping: { [key: string]: string } = {
  en: 'en',
  es: 'es',
  pt: 'pt-pt',
};

const Captcha = function ({ onSuccess }: CaptchaProps): JSX.Element | null {
  const {
    captchaConfig,
    captchaErrorCallback,
    captchaIsLoading,
    captchaIsMounted,
    captchaSuccessCallback,
  } = useCaptcha();

  const { isOnline } = useNetwork();

  const { t } = useTranslation(['ALERTS']);

  // Changing the language will throw an `old challenge` error because of the re-rendering.
  // In `handleError`, we reload the widget to show it with the new language.
  const lang = langMapping[localStorage.getItem(LOCAL_STORAGE.LANGUAGE) || 'en'];

  const handleError = () => {
    // RGCaptcha doesn't return the error
    captchaErrorCallback();
  };

  const handleSuccess = ({
    geetest_challenge: challenge,
    geetest_validate: validate,
    geetest_seccode: seccode,
  }: GeetestUnprocessedSolution) => {
    const solution = {
      challenge,
      validate,
      seccode,
    };
    onSuccess?.(solution);
    captchaSuccessCallback(solution);
  };

  if (!captchaIsMounted || !captchaConfig) {
    return null;
  }

  if (!isOnline) {
    return <span>{t('ALERTS:OFFLINE_CAPTCHA_MESSAGE')}</span>;
  }

  if (captchaIsLoading) {
    return null;
  }

  return (
    <RGCaptcha
      name={CAPTCHA_NAME}
      width="100%"
      onSuccess={handleSuccess}
      onError={handleError}
      data={captchaConfig}
      lang={lang}
      product="float"
    />
  );
};

Captcha.defaultProps = {
  onSuccess: undefined,
};

export default Captcha;
